// src/App.js
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { HashRouter as Router, Routes, Route, Link } from "react-router-dom";

import Stars from "./pages/Stars";
import Quizzes from "./pages/Quizzes";
import Episodes from "./pages/Episodes";
import Horoscope from "./pages/Horoscope";
import Miscellaneous from "./pages/Miscellaneous";
import GoodbyeAlex from "./pages/GoodbyeAlex";
import Checklist from "./pages/Checklist";
import McBitch from "./pages/McBitch";
import TenPM from "./pages/TenPM";
import Ifwyou from "./pages/Ifwyou";
import Moons from "./pages/Moons";
import WitchQuiz from "./pages/WitchQuiz";
import FriendsgivingQuiz from "./pages/FriendsgivingQuiz";
import Strikeout from "./pages/Strikeout";
import Karmaquiz from "./pages/Karmaquiz";
import Real from "./pages/Real";
import GuessWhatGame from "./pages/GuessWhatGame";

import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import "./App.css";

// existing main-page art
import moonImage from "./assets/moonbright1.JPEG";
import cloud1 from "./assets/cloud1.PNG";
import cloud2 from "./assets/cloud2.PNG";
import yawningmoon from "./assets/yawningmoon.PNG";
import bikingfish from "./assets/bikingfish.png";

const STORAGE_KEY = "wednesdaynights_goal_moons_v1";

// Keep stable constants OUTSIDE components to avoid hook dependency warnings
const STARRY_PLOUGH = [-122.2660, 37.8530]; // [lng, lat]

// Use demo style for now (no keys). Once it renders reliably, swap to Stadia Toner.
// const STADIA_TONER_STYLE = (key) =>
//   `https://tiles.stadiamaps.com/styles/stamen_toner.json?api_key=${key}`;
const STADIA_KEY = "e559e5e2-cd45-44e7-988f-b186e65f7642";
const STADIA_STYLE_URL = `https://tiles.stadiamaps.com/styles/stamen_toner.json?api_key=${STADIA_KEY}`;
const FALLBACK_STYLE_URL = "https://demotiles.maplibre.org/style.json";

/**
 * Map component that is React-safe:
 * - uses useLayoutEffect so the container has layout before map init
 * - calls resize after load to avoid the "black until resize" issue
 */
function TonerStarryPloughMap() {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) return;

    const styleUrl = STADIA_KEY ? STADIA_STYLE_URL : FALLBACK_STYLE_URL;
    console.log("MAP styleUrl:", styleUrl);

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl,
      center: STARRY_PLOUGH,
      zoom: 15.8,
      pitch: 0,
      bearing: 0,
    });

    mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), "top-right");

    const handleResize = () => map.resize();
    window.addEventListener("resize", handleResize);

    map.on("error", (e) => {
      console.log("MAP ERROR FULL:", e);
      console.log("MAP ERROR MESSAGE:", e?.error?.message);
    });

    map.on("load", () => {
      console.log("MAP LOADED ✅");
      requestAnimationFrame(() => map.resize());

      // --- PURPLE RING MARKER (HTML overlay; super reliable in prod) ---
      const ring = document.createElement("div");
      ring.style.width = "22px";
      ring.style.height = "22px";
      ring.style.borderRadius = "999px";
      ring.style.border = "3px solid rgba(180, 90, 255, 0.95)";
      ring.style.background = "rgba(255,255,255,0.9)";
      ring.style.boxShadow = "0 0 0 8px rgba(180, 90, 255, 0.22), 0 8px 18px rgba(0,0,0,0.35)";
      ring.style.transform = "translate(-50%, -50%)";

      new maplibregl.Marker({ element: ring, anchor: "center" })
        .setLngLat(STARRY_PLOUGH)
        .addTo(map);

      // --- LABEL MARKER ---
      const label = document.createElement("div");
      label.textContent = "The Starry Plough";
      label.style.fontFamily = "Times New Roman, Times, serif";
      label.style.fontSize = "14px";
      label.style.fontWeight = "700";
      label.style.color = "rgba(180, 90, 255, 1)";
      label.style.textShadow = "0 0 6px rgba(255,255,255,0.95), 0 0 2px rgba(255,255,255,0.95)";
      label.style.whiteSpace = "nowrap";
      label.style.transform = "translate(-50%, -100%)";
      label.style.marginTop = "-10px";

      new maplibregl.Marker({ element: label, anchor: "bottom" })
        .setLngLat(STARRY_PLOUGH)
        .addTo(map);

      // Optional UX
      map.scrollZoom.disable();
    });

    return () => {
      window.removeEventListener("resize", handleResize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div className="mystic-map-wrap">
      <div ref={containerRef} className="mystic-map" />
      <div className="mystic-map-caption">drag to explore · scroll zoom disabled (on purpose)</div>
    </div>
  );
}


function App() {

  const [completed, setCompleted] = useState({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setCompleted(JSON.parse(raw));
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(completed));
    } catch {
      // ignore
    }
  }, [completed]);



  return (
    <Router>
      <div>
        {/* Top Section */}
        <div className="top-section">
          <Link to="/" className="site-title">
            Wednesday Nights
          </Link>
          <div className="nav-links-slim">
            <Link to="/Stars">Stars</Link>
            <Link to="/Quizzes">Quizzes</Link>
            <Link to="/Episodes">Episodes</Link>
            <Link to="/horoscope">Horoscope</Link>
            <Link to="/miscellaneous">Miscellaneous</Link>
          </div>
        </div>

        <Routes>
          <Route
            path="/"
            element={
              <div className="main-page">
                <div className="main-content">
                  {/* existing art */}
                  <img src={moonImage} alt="Moon" className="moon-image" />
                  <img src={cloud1} alt="Cloud1" className="cloud1-image" />
                  <img src={cloud2} alt="Cloud2" className="cloud2-image" />
                  <img src={yawningmoon} alt="YawnMoon" className="yawning-moon-image" />
                  <img src={bikingfish} alt="Bikefish" className="bikingfish-image" />

                  <div className="new-year">
                    <h2>New Year, new Sos.</h2>
                  </div>

                 <div className="maddy-moon">
  <Link to="/Stars" className="maddy-moon-link">
    <h2>click here for your salvation! </h2>
  </Link>
</div>



                  <div className="next-moon">
                    <h2>NEXT FULL MOON: OCTOBER 26</h2>
                  </div>



                  <div className="yawn-comm">
                    <h2>
                      this yawning moon is me right now im so tired. at least the moon gets to sleep all
                      day i have to be at work.
                    </h2>
                  </div>

                  {/* ✅ The Map */}
                  <TonerStarryPloughMap />

                  
                  <div className="Checklist">
                    <Checklist />
                  </div>

                
                </div>
              </div>
            }
          />

          {/* other routes unchanged */}
          <Route path="/Stars" element={<Stars />} />
          <Route path="/Quizzes" element={<Quizzes />} />
          <Route path="/Episodes" element={<Episodes />} />
          <Route path="/horoscope" element={<Horoscope />} />
          <Route path="/miscellaneous" element={<Miscellaneous />} />
          <Route path="/mercury" element={<GoodbyeAlex />} />
          <Route path="/Checklist" element={<Checklist />} />
          <Route path="/pollux" element={<McBitch />} />
          <Route path="/lea" element={<TenPM />} />
          <Route path="/northstar" element={<Ifwyou />} />
          <Route path="/sirius" element={<Moons />} />
          <Route path="/witchquiz" element={<WitchQuiz />} />
          <Route path="/rue" element={<Strikeout />} />
          <Route path="/karmaquiz" element={<Karmaquiz />} />
          <Route path="/jonas" element={<Real />} />
          <Route path="/naya" element={<GuessWhatGame />} />
          <Route path="/friendsgiving" element={<FriendsgivingQuiz />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
